import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { noop, without } from 'lodash';
import { uuid } from '@/utils/misc';
import Toast, {
  ToastMessage,
  ToastMessageContent,
  ToastVariant,
} from '@/components/Common/Toast';

type ToastContext = {
  messages: ToastMessage[];
  showMessage: (
    message: ToastMessageContent,
    variant?: ToastVariant,
    duration?: number
  ) => void;
  showError: (message: ToastMessageContent) => void;
};

const Context = createContext<ToastContext>({
  messages: [],
  showMessage: noop,
  showError: noop,
});

export const useToast = () => useContext(Context);

const defaultDuration = 5000;

const ToastProvider: FC<PropsWithChildren> = ({ children }) => {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const handleAdd = useCallback((config: { message: ToastMessage; duration: number }) => {
    setMessages((current) => [...current, config.message]);
    setTimeout(() => handleRemove(config.message), config.duration);
  }, []);

  const handleRemove = (message: ToastMessage) => {
    setMessages((messages) => without(messages, message));
  };

  const handleMessage = useCallback(
    (
      message: ToastMessageContent,
      type: ToastVariant = ToastVariant.Regular,
      duration = defaultDuration
    ) =>
      handleAdd({
        message: { ...message, type, id: uuid() },
        duration,
      }),
    [handleAdd]
  );

  const showError = useCallback(
    (message: ToastMessageContent) =>
      handleAdd({
        message: { ...message, type: ToastVariant.Error, id: uuid() },
        duration: 50000000,
      }),
    [handleAdd]
  );

  return (
    <Context.Provider
      value={{
        messages,
        showMessage: handleMessage,
        showError,
      }}
    >
      <Toast messages={messages} onClear={handleRemove} />
      {children}
    </Context.Provider>
  );
};

export default ToastProvider;
